import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link, useLocation, useNavigationType } from 'react-router-dom';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { useSelector } from 'react-redux';
import ErrorBoundary from '../components/ErrorBoundary.mjs';

// Lazy-loaded routes — only downloaded when user navigates to /admin or /q/editor.
// Chunk load failures are handled centrally by ErrorBoundary (auto-reload once).
const EditorPage = React.lazy(() => import('../pages/EditorPage.mjs'));

import MyReviews from '../pages/MyReviews.mjs';

import UnifiedArticleDetailPage from '../pages/UnifiedArticleDetailPage.mjs';
import HierarchicalRouteDispatcher from '../routing/HierarchicalRouteDispatcher.mjs';
import Login from '../components/auth/Login.mjs';
import Signup from '../components/auth/Signup.mjs';
import AccountSettings from '../components/auth/AccountSettings.mjs';
const AdminDashboard = React.lazy(() => import('../components/admin/AdminDashboard.mjs'));
import CategoryPage from '../pages/CategoryPage.mjs';
import ForgotPassword from '../components/auth/ForgotPassword.mjs';
import ResetPassword from '../components/auth/ResetPassword.mjs';
import EmailVerification from '../components/auth/EmailVerification.mjs';
import MainLayout from '../layouts/MainLayout.mjs';
import CategoryHomePage from '../pages/CategoryHomePage.mjs';

import TopicPage from '../pages/TopicPage.mjs';
import ForYouPage from '../pages/ForYouPage.mjs';
import LandingPage from '../pages/LandingPage.mjs';

import AppModule from '../pages/App.mjs';
import { connectWebSocket } from '../js/clientSockets.mjs';
import { PageViewTracker } from '../js/ga4.mjs';

const { App } = AppModule;

// Grab preloaded Redux state from the server
const preloadedState = window.__PRELOADED_STATE__ || {};

// DEV-only: keep a WebSocket open so the client can detect server restarts
if (process.env.NODE_ENV === 'development') {
    connectWebSocket()
        .then(() => console.log('Dev mode: WebSocket connected.'))
        .catch((err) => console.error('Dev mode: WebSocket failed:', err));
}

// --------------------------------------------------
// Smart Scroll Restoration (works with BrowserRouter)
// Scrolls to top on PUSH navigation, restores position on POP (back/forward)
// --------------------------------------------------
const ScrollToTop = () => {
    const { pathname } = useLocation();
    const navigationType = useNavigationType(); // 'PUSH', 'POP', 'REPLACE'

    useEffect(() => {
        // Only scroll to top on forward navigation (PUSH) or replace
        // Let browser handle POP (back/forward) naturally
        if (navigationType !== 'POP') {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [pathname, navigationType]);

    return null;
};

// --------------------------------------------------
// 2) Admin‐role guard remains the same
// --------------------------------------------------
const AdminRouteWrapper = ({ children }) => {
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const userRole = useSelector((state) => state.auth.user?.role);
    const isLoading = useSelector((state) => state.auth.loading);

    if (isLoading) return <div>Loading…</div>;

    if (!isAuthenticated) {
        const returnTo = encodeURIComponent(window.location.pathname);
        return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
    }

    if (userRole !== 'admin') {
        return (
            <div className="container mx-auto px-4 py-8 text-center bg-theme-surface">
                <h1 className="text-2xl font-bold text-theme-error">Unauthorized</h1>
                <p className="text-theme-secondary">You do not have permission to access this page.</p>
                <Link to="/" className="btn mt-4">
                    Go to Home
                </Link>
            </div>
        );
    }

    return children ?? <Outlet />;
};

// --------------------------------------------------
// 3) AccountDeletedPage (no <App> wrapper here)
// --------------------------------------------------
const AccountDeletedPageComponent = () => (
    <div className="account-deleted-container">
        <h1>Account Successfully Deleted</h1>
        <p>Your account has been deleted. We're sorry to see you go!</p>
        <a href="/" className="btn">
            Return to Home
        </a>
    </div>
);

// --------------------------------------------------
// 4) Route configuration arrays
// --------------------------------------------------
const routesConfig = [
    { path: 'my-reviews', Component: MyReviews },
    { path: 'account-deleted', Component: AccountDeletedPageComponent },
    { path: 'account', Component: AccountSettings },
    { path: 'articles/:slug', Component: UnifiedArticleDetailPage },  // SEO-friendly slug-based URL
    { path: 'article/:slug', Component: UnifiedArticleDetailPage },   // Alternate route
    { path: 'category/:categoryId', Component: CategoryPage },
    { path: 'editor', Component: () => <Suspense fallback={<div className="text-theme-secondary text-center py-8">Loading editor…</div>}><EditorPage /></Suspense> },
    { path: 'forgot-password', Component: ForgotPassword },
    { path: 'login', Component: Login },
    { path: 'reset-password/:token', Component: ResetPassword },
    { path: 'signup', Component: Signup },
    { path: 'verify-email/:token', Component: EmailVerification },
];

const adminRoutesConfig = [
    { index: true, Component: AdminDashboard },
    { path: 'dashboard', Component: AdminDashboard },
    // Core tabs
    { path: 'content-pipeline', Component: AdminDashboard },
    { path: 'search-index', Component: AdminDashboard },
    { path: 'plugin-health', Component: AdminDashboard },
    // Plugin tabs
    { path: 'cms', Component: AdminDashboard },
    { path: 'cms2', Component: AdminDashboard },
    { path: 'category', Component: AdminDashboard },
    { path: 'topic', Component: AdminDashboard },
    { path: 'tag', Component: AdminDashboard },
    { path: 'article', Component: AdminDashboard },
    { path: 'cacheManagement', Component: AdminDashboard },
    { path: 'userManagement', Component: AdminDashboard },
    { path: 'reviewAnalytics', Component: AdminDashboard },
    { path: 'notification', Component: AdminDashboard },
];



// --------------------------------------------------
// 5) Error boundary fallback components
// --------------------------------------------------

/** Full-page error fallback for Layer #1 (catastrophic failures) */
const FullPageError = (error, resetErrorBoundary) => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2rem',
            textAlign: 'center',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
    >
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💥</div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            Application Error
        </h1>
        <p style={{ color: '#737373', marginBottom: '1.5rem', maxWidth: '400px' }}>
            Something went seriously wrong. This usually fixes itself with a page reload.
        </p>
        <button
            onClick={() => window.location.reload()}
            style={{
                padding: '0.625rem 2rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: '#2563eb',
                color: '#fff',
            }}
        >
            Reload Page
        </button>
    </div>
);

/**
 * Route-level error boundary (Layer #2).
 * Resets automatically when the URL changes via key={location.pathname}.
 * Header/Footer stay visible because this sits inside MainLayout.
 */
const RouteErrorBoundary = () => {
    const location = useLocation();
    return (
        <ErrorBoundary key={location.pathname}>
            <Outlet />
        </ErrorBoundary>
    );
};

// --------------------------------------------------
// 6) AppRoutes no longer calls usePageView() at top level.
//    Instead, it renders <PageViewTracker /> inside <BrowserRouter>.
// --------------------------------------------------
function AppRoutes() {
    return (
        <App preloadedState={preloadedState}>
            {/* Layer #1: Outermost boundary — catches catastrophic failures */}
            <ErrorBoundary fallback={FullPageError}>
                <BrowserRouter>
                    {/* ← This must be _inside_ BrowserRouter */}
                    <PageViewTracker />

                    <Routes>
                        {/* Shared layout: Header + Footer persist across navigations */}
                        <Route element={<MainLayout />}>
                            {/* Layer #2: Route-level boundary — page errors keep Header/Footer visible */}
                            <Route element={<RouteErrorBoundary />}>
                                {/* Home (root) - Dedicated landing page experience */}
                                <Route path="/" element={<LandingPage />} />

                                {/* For You personalized page */}
                                <Route path="/for-you" element={<ForYouPage />} />

                                {/* Category + Topic routes — single parent with index + splat */}
                                {/* NOTE: Server uses Express 5 named splat `*topicPath` (yields array in req.params.topicPath).
                                    Client uses RR7 unnamed splat `*` (yields string via useParams()['*']).
                                    TopicPage.mjs bridges this by splitting the string into segments. */}
                                <Route path="/:categorySlug">
                                    {/* Exact match: /sports → CategoryHomePage */}
                                    <Route index element={<CategoryHomePage />} />
                                    {/* Any deeper path: dispatched to TopicPage or ArticleDetailPage */}
                                    <Route path="*" element={<HierarchicalRouteDispatcher />} />
                                </Route>

                                {/* Auth & view routes (root-level — no /q prefix) */}
                                {routesConfig.map(({ path, Component }) => (
                                    <Route key={path} path={`/${path}`} element={<Component />} />
                                ))}

                                {/* Legacy /q/home → "/" */}
                                <Route path="/q/home" element={<Navigate to="/" replace />} />

                                {/* Admin‐only subtree */}
                                <Route element={<AdminRouteWrapper />}>
                                    <Route path="/admin">
                                        <Route element={
                                            <Suspense fallback={
                                                <div className="text-theme-secondary text-center py-8">Loading admin…</div>
                                            }>
                                                <Outlet />
                                            </Suspense>
                                        }>
                                            {adminRoutesConfig.map(({ path, Component, index }) => (
                                                <Route
                                                    key={index ? 'admin-index' : `admin-${path}`}
                                                    index={index}
                                                    path={path}
                                                    element={<Component />}
                                                />
                                            ))}
                                            <Route path="*" element={<div>Admin page not found</div>} />
                                        </Route>
                                    </Route>
                                </Route>

                                {/* Global fallback */}
                                <Route path="*" element={<div>Page not found</div>} />
                            </Route>
                        </Route>
                    </Routes>

                    {/* Smart scroll restoration: scrolls to top on forward nav, lets browser handle back/forward */}
                    <ScrollToTop />
                </BrowserRouter>
            </ErrorBoundary>
        </App>
    );
}

// --------------------------------------------------
// 6) Hydration vs. CSR logic (unchanged)
// --------------------------------------------------
const rootElement = document.getElementById('root');
let root;

if (rootElement && rootElement.innerHTML !== '' && !rootElement.dataset.hydrated) {
    root = hydrateRoot(rootElement, <AppRoutes />);
    rootElement.dataset.hydrated = 'true';
} else if (rootElement) {
    root = createRoot(rootElement);
    root.render(<AppRoutes />);
} else {
    console.error('Root element #root not found.');
}
